import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import CounselSurveyForm, { buildSurveyAnswerInputs } from '../components/CounselSurveyForm'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'

function unwrap(res) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
}

export default function CounselSurveySessionRunPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const sessionQuery = useQuery({
    queryKey: ['counsel-surveys', 'session-run', id],
    queryFn: async () => unwrap(await api.get(`/counsel-surveys/sessions/${id}`, { __silent: true })),
  })

  const saveMutation = useMutation({
    mutationFn: async ({ answers, progressPercent }) => unwrap(await api.patch(`/counsel-surveys/sessions/${id}/draft`, {
      progressPercent,
      answers,
    })),
    onSuccess: () => {
      try { window?.toast?.show?.('설문을 임시저장했습니다.', 'success') } catch {}
      sessionQuery.refetch()
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '임시저장 실패', 'error') } catch {}
    },
  })

  const submitMutation = useMutation({
    mutationFn: async ({ answers }) => unwrap(await api.post(`/counsel-surveys/sessions/${id}/submit`, {
      progressPercent: 100,
      answers,
    })),
    onSuccess: async () => {
      try { window?.toast?.show?.('설문을 제출했습니다. 분석 리포트 화면으로 이동합니다.', 'success') } catch {}
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'sessions', 'hub'] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'session-detail', id] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'session-report', id] }),
      ])
      navigate(`/support/surveys?tab=history&openSession=${id}&focusReport=1`)
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '설문 제출 실패', 'error') } catch {}
    },
  })

  if (sessionQuery.isLoading) return <Loading />
  if (sessionQuery.isError) return <InlineError message={sessionQuery.error?.message || '설문 세션을 불러오지 못했습니다.'} />

  const session = sessionQuery.data || {}
  const schema = session.survey || { title: '고객 설문', sections: [] }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm text-slate-500">고객지원 &gt; 설문관리</div>
          <div className="mt-1 text-lg font-semibold">
            {session.templateName || '고객 설문'} {session.customerName ? `- ${session.customerName}` : ''}
          </div>
        </div>
        <Link to="/support/surveys?tab=history" className="rounded border px-3 py-2 text-sm hover:bg-slate-50">
          이력으로 돌아가기
        </Link>
      </div>

      <CounselSurveyForm
        schema={schema}
        initialAnswers={session.answers}
        saving={saveMutation.isPending}
        submitting={submitMutation.isPending}
        onSave={async (answerMap, progressPercent) => {
          await saveMutation.mutateAsync({
            progressPercent,
            answers: buildSurveyAnswerInputs(schema, answerMap),
          })
        }}
        onSubmit={async (answerMap) => {
          await submitMutation.mutateAsync({
            answers: buildSurveyAnswerInputs(schema, answerMap),
          })
        }}
      />
    </div>
  )
}
